import { useMemo } from 'react';

import { useElementShown } from '@hh.ru/analytics-js';
import skillMatchElementShown from '@hh.ru/analytics-js-events/build/career_platform/profession/skill_match_element_shown';
import { translation, format, TranslatedComponent } from '@hh.ru/front-static-app';
import { Text, Card } from '@hh.ru/magritte-ui';

import ProgressCircle from 'src/components/ProgressCircle';
import { IndicationResponse } from 'src/models/pages/profession';
import { ProfessionId } from 'src/types/profession';
import pluralize from 'src/utils/pluralize';

import styles from './styles.less';

const TrlKeys = {
    circle: {
        skill: 'career-platform.profession.card-match-skills.circle.skill',
    },
    needed: {
        one: 'career-platform.profession.card-match-skills.match.needed.one',
        some: 'career-platform.profession.card-match-skills.match.needed.some',
        many: 'career-platform.profession.card-match-skills.match.needed.many',
    },
    valueIn: {
        le0: {
            title: 'career-platform.profession.card-match-skills.match.e0.title',
            description: 'career-platform.profession.card-match-skills.match.e0.description',
        },
        le20: {
            title: 'career-platform.profession.card-match-skills.match.le20.title',
            description: 'career-platform.profession.card-match-skills.match.le20.description',
        },
        le50: {
            title: 'career-platform.profession.card-match-skills.match.le50.title',
            description: 'career-platform.profession.card-match-skills.match.le50.description',
        },
        le70: {
            title: 'career-platform.profession.card-match-skills.match.le70.title',
            description: 'career-platform.profession.card-match-skills.match.le70.description',
        },
        le80: {
            title: 'career-platform.profession.card-match-skills.match.le80.title',
            description: 'career-platform.profession.card-match-skills.match.le80.description',
        },
        le100: {
            title: 'career-platform.profession.card-match-skills.match.e100.title',
            description: 'career-platform.profession.card-match-skills.match.e100.description',
        },
    },
};

interface Props {
    professionId: ProfessionId;
    values: IndicationResponse;
}

const CardMatchSkills: TranslatedComponent<Props> = ({
    trls,
    professionId,
    values: { matchPercent, professionCountSkill, userCountSkill },
}) => {
    const trlKeyByPercent = useMemo(
        () =>
            Object.keys(TrlKeys.valueIn).find(
                (k) => matchPercent <= parseInt(k.replace('le', ''), 10)
            ) as keyof typeof TrlKeys.valueIn,
        [matchPercent]
    );

    const translations = TrlKeys.valueIn[trlKeyByPercent];

    const ref = useElementShown(skillMatchElementShown, {
        professionId,
        skillMatch: matchPercent,
    });

    return (
        <Card ref={ref} style="contrast" padding={16} borderRadius={24} stretched data-qa="card-match-skills">
            <ProgressCircle style="neutral" percent={matchPercent} size={80} strokeWidth={6}>
                <div className={styles.circleContent}>
                    <Text typography="subtitle-1-semibold" style="contrast" Element="span">
                        {matchPercent}%
                    </Text>
                    <div className={styles.circleText}>
                        <Text typography="paragraph-4-regular" style="secondary" Element="span">
                            {trls[TrlKeys.circle.skill]}
                        </Text>
                    </div>
                </div>
            </ProgressCircle>
            <div className={styles.title}>
                <Text typography="subtitle-1-semibold" Element="p">
                    {trls[translations.title]}
                </Text>
            </div>
            <Text typography="paragraph-3-regular" Element="p" data-qa="card-match-skills-text">
                {format(trls[translations.description], {
                    '{0}': userCountSkill,
                    '{1}': professionCountSkill,
                    '{2}': trls[
                        pluralize({
                            count: userCountSkill,
                            one: TrlKeys.needed.one,
                            some: TrlKeys.needed.some,
                            many: TrlKeys.needed.many,
                        })
                    ],
                })}
            </Text>
        </Card>
    );
};

export default translation(CardMatchSkills);
